import React from "react";
import { Button, IconButton, Tooltip } from "@material-ui/core";
import { alpha, makeStyles } from "@material-ui/core/styles";
import { X, HardDrive as HardDriveIcon } from "lucide-react";
import { useHistory } from "react-router-dom";
import useDiskUsageAlerts from "../../hooks/useDiskUsageAlerts";
import { i18n } from "../../translate/i18n";

const LEVELS = {
  warning: { main: "#f59e0b", dark: "#d97706" },
  critical: { main: "#f97316", dark: "#ea580c" },
  full: { main: "#ef4444", dark: "#dc2626" },
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const level = (key) => {
    const c = LEVELS[key];
    const accent = dark ? c.main : c.dark;
    return {
      "--disk-accent": accent,
      "--disk-accent-soft": alpha(c.main, dark ? 0.14 : 0.1),
      "--disk-accent-border": alpha(c.main, dark ? 0.35 : 0.28),
    };
  };
  return {
    banner: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      minHeight: 40,
      padding: theme.spacing(0.5, 1, 0.5, 1.25),
      margin: theme.spacing(1, 2, 0, 2),
      flexShrink: 0,
      borderRadius: 10,
      border: "1px solid var(--disk-accent-border)",
      background: dark
        ? `linear-gradient(90deg, var(--disk-accent-soft) 0%, ${theme.palette.background.paper} 55%)`
        : `linear-gradient(90deg, var(--disk-accent-soft) 0%, ${theme.palette.background.contrast} 55%)`,
      boxShadow: dark ? "0 1px 4px rgba(0,0,0,0.25)" : "none",
      color: theme.palette.text.primary,
      "&.warning": level("warning"),
      "&.critical": level("critical"),
      "&.full": level("full"),
    },
    iconWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: 8,
      flexShrink: 0,
      color: "var(--disk-accent)",
      backgroundColor: "var(--disk-accent-soft)",
      border: "1px solid var(--disk-accent-border)",
    },
    text: {
      display: "flex",
      alignItems: "baseline",
      gap: theme.spacing(1),
      minWidth: 0,
      flex: "1 1 auto",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    title: {
      fontSize: "0.85rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: "var(--disk-accent)",
      flexShrink: 0,
    },
    detail: {
      fontSize: "0.78rem",
      color: theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    track: {
      position: "relative",
      width: 120,
      height: 4,
      borderRadius: 4,
      flexShrink: 0,
      backgroundColor: dark
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.08)",
      overflow: "hidden",
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    fill: {
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      borderRadius: 4,
      backgroundColor: "var(--disk-accent)",
      transition: "width 0.4s ease",
    },
    actions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      flexShrink: 0,
    },
    manageButton: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.78rem",
      padding: theme.spacing(0.25, 1.25),
      borderRadius: 8,
      color: "var(--disk-accent)",
      "&:hover": { backgroundColor: "var(--disk-accent-soft)" },
    },
    closeButton: {
      color: theme.palette.text.secondary,
      padding: 4,
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
  };
});

const DiskUsageAlert = () => {
  const classes = useStyles();
  const history = useHistory();
  const { alert, dismissAlert } = useDiskUsageAlerts();

  if (!alert) {
    return null;
  }

  const percent = Number(alert.usagePercent) || 0;
  const title =
    alert.level === "full"
      ? i18n.t("storageManagement.alerts.full")
      : i18n.t(`storageManagement.alerts.${alert.level}`, {
          percent: percent.toFixed(1),
        });

  return (
    <div
      role="status"
      aria-live="polite"
      className={`${classes.banner} ${alert.level}`}
    >
      <div className={classes.iconWrap}>
        <HardDriveIcon size={15} strokeWidth={2.2} />
      </div>

      <div className={classes.text}>
        <span className={classes.title}>{title}</span>
        {alert.message && (
          <span className={classes.detail}>{alert.message}</span>
        )}
      </div>

      <div
        className={classes.track}
        aria-hidden="true"
        title={`${percent.toFixed(1)}%`}
      >
        <div
          className={classes.fill}
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>

      <div className={classes.actions}>
        <Button
          size="small"
          onClick={() => history.push("/storage-management")}
          className={classes.manageButton}
        >
          {i18n.t("storageManagement.manage")}
        </Button>
        <Tooltip title={i18n.t("storageManagement.cancel")}>
          <IconButton
            aria-label="close"
            size="small"
            onClick={dismissAlert}
            className={classes.closeButton}
          >
            <X size={15} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export default DiskUsageAlert;
